import ks from "../ReUsableTS/kBrowserShared";
import { kObject, kObjectArray } from "../ReUsableTS/kBrowserObjects";

import { kMotion } from "./kMotion";
// Re-exported so existing importers of kAnimateTexts keep working.
export { kMotion } from "./kMotion";

export class kAnimTextObj extends kObject{
    public NewText = "";
    public Object: HTMLElement | null = null;
    public HTMLID:string = "";
    public CurrentNumber:number = 0;
    /** Half-cycle length. The out-swing and the in-swing each take this long,
     *  so a full swap costs roughly 2x. Default 300 preserves the original
     *  timing for every existing caller; a component with a fast cadence
     *  (a status line changing every 600ms) should lower it. */
    public DurationMs: number = 300;
    private SwapTimer: number = 0;

    /** Kept as the name every component already calls; the DECISION now lives in
     *  `kMotion`, so there is one switch rather than five copies of a matchMedia
     *  call. Returns false unless a project has opted in - the components animate
     *  by default whatever the OS setting says. See the kMotion class comment. */
    public static PrefersReducedMotion = (): boolean => {
        if (ks.KErrorMode === true) { return false; }
        try {
            return kMotion.IsReduced();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAnimTextObj.PrefersReducedMotion");
            return false;
        }
    };

    /** A transform animation on a `display:inline` element runs perfectly and
     *  MOVES NOTHING - `transform` does not apply to non-replaced inline boxes
     *  (CSS Transforms 1, sec. 3). The animation still ticks, `getAnimations()`
     *  still returns it, and `getComputedStyle().transform` still reports the
     *  interpolated matrix, so every way of asking "is it animating?" says yes
     *  while the pixels never budge. Confirmed 2026-07-31 in Edge: the demo's
     *  plain <span> painted 12 distinct matrices during a swap and held a
     *  constant 43px width the whole way through.
     *
     *  Upgrading to inline-block is the smallest fix that makes the box
     *  transformable. It is deliberately NOT set in a stylesheet: this class is
     *  handed arbitrary host elements, and only the ones it actually animates
     *  should be touched. For single-line text the visible difference is nil -
     *  an inline-block still sits on the baseline - the one real change being
     *  that the element can no longer wrap mid-way across a line, which for a
     *  value that is about to scale is the better behaviour anyway. */
    public EnsureTransformable = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.Object === null) { return; }
            // Only `inline` is upgraded. block/flex/grid/inline-block already
            // take a transform; `display:contents` and table boxes cannot be
            // fixed this way and are left alone rather than silently relaid out.
            if (window.getComputedStyle(this.Object).display !== "inline") { return; }
            this.Object.style.display = "inline-block";
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAnimTextObj.EnsureTransformable");
        }
    };

    /** Drop a pending swap so rapid successive updates cannot stack timers and
     *  fight each other over the same element. */
    public ClearPendingSwap = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (this.SwapTimer !== 0) {
                window.clearTimeout(this.SwapTimer);
                this.SwapTimer = 0;
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAnimTextObj.ClearPendingSwap");
        }
    };

    public FadeIn = (): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            this.SwapTimer = 0;
            if(this.Object){
                this.Object.textContent = this.NewText;
                this.EnsureTransformable();
                if(window.getComputedStyle(this.Object).transform === "none"){
                    this.Object.animate([{ transform: "scale(0)" },{ transform: "scale(1)" }],{duration: this.DurationMs,iterations: 1});
                }
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAnimTextObj.FadeIn");
        }
    }
    public UpdateNumber = (Up:boolean): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            if(this.Object){
                
                if(Up){
                    this.CurrentNumber++
                }else{
                    this.CurrentNumber--;
                }
                this.UpdateText(this.CurrentNumber.toString());
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAnimTextObj.UpdateNumber");
        }
    }  
    public Increment = (): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            this.UpdateNumber(true);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAnimTextObj.Increment");
        }
    }
    public Decrement= (): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            this.UpdateNumber(false);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAnimTextObj.Decrement");
        }
    }
    public UpdateText = (NewText: string): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            if (this.Object === null) { return; }
            const CurrText: string = ks.Nz(this.Object.textContent);
            if (CurrText === NewText) { return; }
            this.NewText = NewText;
            if (kAnimTextObj.PrefersReducedMotion() === true) {
                // The text IS the information; the scale swap is decoration.
                this.ClearPendingSwap();
                this.Object.textContent = NewText;
                return;
            }
            this.EnsureTransformable();
            if (window.getComputedStyle(this.Object).transform === "none") {
                this.Object.animate([{ transform: "scale(1)" }, { transform: "scale(0)" }],{duration: this.DurationMs,iterations: 1});
            }
            // Replace any in-flight swap rather than stacking a second timer -
            // a fast caller (a per-item progress line) would otherwise queue
            // several FadeIns that fight over the same element.
            this.ClearPendingSwap();
            this.SwapTimer = window.setTimeout(this.FadeIn, this.DurationMs + 10);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAnimTextObj.UpdateText");
        }
    };
}
export class kAnimTextObjs extends kObjectArray{
    public Items: kAnimTextObj[] = [];
    public UpdateText = (HTMLID: string, NewText: string): void => {
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            let CurrItem: kAnimTextObj|null = this.GetItemByHTMLID(HTMLID);
            if(CurrItem) {
                    CurrItem.UpdateText(NewText);
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAnimTextObjs.UpdateText");
        }
    }
    // Renamed from getAnimObjByObj 2026-08-06. Verified by an estate-wide scan that
    // EnsureByObj below is the ONLY caller — every other hit was a private static of
    // the same name in a product's own frozen lib/kalmstromShared4.ts copy, which does
    // not reach this class.
    public GetAnimObjByObj = (
        Object: HTMLElement | null | undefined
    ): kAnimTextObj | null => {
        if (ks.KErrorMode === true) {
            return null;
        }
        try {
            for (const Item of this.Items) {
                if (Item.Object === Object) { return Item; }
            }
            return null;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAnimTextObjs.GetAnimObjByObj");
            return null;
        }
    };
    public GetItemByHTMLID = (HTMLID:string): kAnimTextObj | null => {
        if (ks.KErrorMode === true) {
            return null;
        }
        try {
            for (const Item of this.Items) {
                if (Item.HTMLID === HTMLID) { return Item; }
            }
            return null;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAnimTextObjs.GetItemByHTMLID");
            return null;
        }
    };
    public KReplaceText = (
        ID: string,
        NewText: string,
        Object?: HTMLElement | null
    ): void => {
        //can be called with either an ID or a dom object to animate a text change
        if (ks.KErrorMode === true) {
            return;
        }
        try {
            let CurrObj: kAnimTextObj | null = null;
            if (ID !== undefined) {
                CurrObj = this.EnsureById(ID);
            } else {
                CurrObj = this.EnsureByObj(Object);
            }
            if (null !== CurrObj) {
                CurrObj.UpdateText(NewText);
            }
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAnimTextObjs.KReplaceText");
        }
    };

    // Look up the cached anim entry for an element id, or create one. After
    // the lookup, refresh `.Object` if the cached node has been detached
    // from the document — host elements that get fully rebuilt between
    // calls would otherwise animate an off-document orphan.
    public EnsureById = (ID: string): kAnimTextObj | null => {
        if (ks.KErrorMode === true) { return null; }
        try {
            let CurrObj: kAnimTextObj | null = this.GetItemByHTMLID(ID);
            if (!ks.KNotNullOrUndefined(CurrObj)) {
                CurrObj = new kAnimTextObj();
                CurrObj.Object = document.getElementById(ID);
                CurrObj.HTMLID = ID;
                this.push(CurrObj);
                return CurrObj;
            }
            if (CurrObj !== null) {
                if (CurrObj.Object === null ||
                    document.body.contains(CurrObj.Object) === false) {
                    CurrObj.Object = document.getElementById(ID);
                }
            }
            return CurrObj;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAnimTextObjs.EnsureById");
            return null;
        }
    };

    public EnsureByObj = (Object?: HTMLElement | null): kAnimTextObj | null => {
        if (ks.KErrorMode === true) { return null; }
        try {
            let CurrObj: kAnimTextObj | null = this.GetAnimObjByObj(Object);
            if (!ks.KNotNullOrUndefined(CurrObj)) {
                CurrObj = new kAnimTextObj();
                if (null !== Object && Object !== undefined) {
                    CurrObj.Object = Object;
                }
                this.push(CurrObj);
            }
            return CurrObj;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAnimTextObjs.EnsureByObj");
            return null;
        }
    };

    /** Drop the cached entry for an element and cancel any pending swap on it.
     *  A component calls this from Destroy so a torn-down node is not kept
     *  alive by this registry and no timer fires against a detached element. */
    public Forget = (Object: HTMLElement | null): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            const Kept: kAnimTextObj[] = [];
            for (const Item of this.Items) {
                if (Item.Object === Object) { Item.ClearPendingSwap(); }
                else { Kept.push(Item); }
            }
            this.Items = Kept;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAnimTextObjs.Forget");
        }
    };

    /** Cancel every pending swap and empty the registry. */
    public ForgetAll = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            for (const Item of this.Items) { Item.ClearPendingSwap(); }
            this.Items = [];
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAnimTextObjs.ForgetAll");
        }
    };
}

/**
 * kAnimatedTextBase - the shared "text never changes abruptly" behaviour,
 * lifted out of kAnimTextObj so every component that displays CHANGING text
 * inherits one implementation instead of each writing `El.textContent = x`.
 *
 * This exists because of constitution rule 2 (lift to a generic base on the
 * SECOND copy): the progress components - status lines, operation logs, step
 * trails, per-file upload rows, ring legends - were each swapping text
 * directly, so a status update landed as an instant jump with nothing to draw
 * the eye. Routing them all through here means a changing value always
 * ANIMATES, which is the whole point of a progress surface.
 *
 * Usage:
 *     export class kThing extends kAnimatedTextBase { ... }
 *     this.SetAnimatedText(this.StatusEl, "Fetching items");
 *     this.SetAnimatedNumber(this.CountEl, 1999);   // -> "1,999"
 *     // in Destroy():
 *     this.DisposeAnimatedText();
 *
 * Honours prefers-reduced-motion (via kAnimTextObj) and formats numbers to the
 * workspace rule, so a subclass gets both for free.
 */
export class kAnimatedTextBase {

    /** Per-instance registry, so two live components never share swap state. */
    protected AnimTexts: kAnimTextObjs = new kAnimTextObjs();

    /** Half-cycle for this component's text swaps. A subclass with a fast
     *  cadence should lower it in its constructor. */
    protected AnimTextDurationMs: number = 300;

    /** Animate `El` to `Text`. Identical text is a no-op, so calling this on a
     *  render loop is safe. */
    protected SetAnimatedText = (El: HTMLElement | null, Text: string): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (El === null) { return; }
            const Entry: kAnimTextObj | null = this.AnimTexts.EnsureByObj(El);
            if (Entry === null) { return; }
            Entry.DurationMs = this.AnimTextDurationMs;
            // A -> B -> A inside one half-cycle. kAnimTextObj.UpdateText compares
            // the request against the DOM, so this second call would return early
            // and leave the queued swap to B in flight - the element would end up
            // showing B for ever (a hold button tapped too quickly keeps its
            // "Keep holding" caption). The element already reads A, so the right
            // answer is to drop the queued swap, not to schedule another.
            if (ks.Nz(El.textContent) === Text) { Entry.ClearPendingSwap(); return; }
            Entry.UpdateText(Text);
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAnimatedTextBase.SetAnimatedText");
        }
    };

    /** Animate `El` to a formatted number. Display rule: thousands separator at
     *  >= 1000, no decimals above 10, two below. */
    protected SetAnimatedNumber = (El: HTMLElement | null, Value: number): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.SetAnimatedText(El, kAnimatedTextBase.FormatDisplayNumber(Value));
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAnimatedTextBase.SetAnimatedNumber");
        }
    };

    /** Set text with NO animation - for the very first paint, where there is
     *  no previous value to transition away from. */
    protected SetTextImmediate = (El: HTMLElement | null, Text: string): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            if (El === null) { return; }
            this.AnimTexts.Forget(El);
            El.textContent = Text;
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAnimatedTextBase.SetTextImmediate");
        }
    };

    /** Call from the subclass's Destroy: cancels pending swaps and releases the
     *  element references this base was holding. */
    protected DisposeAnimatedText = (): void => {
        if (ks.KErrorMode === true) { return; }
        try {
            this.AnimTexts.ForgetAll();
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAnimatedTextBase.DisposeAnimatedText");
        }
    };

    public static FormatDisplayNumber = (Value: number): string => {
        if (ks.KErrorMode === true) { return ""; }
        try {
            if (isFinite(Value) === false) { return "0"; }
            if (Math.abs(Value) > 10) {
                return Value.toLocaleString(undefined, { maximumFractionDigits: 0 });
            }
            return Value.toLocaleString(undefined, { maximumFractionDigits: 2 });
        } catch (e) {
            ks.KGlobalErrorHandler(e, "kAnimatedTextBase.FormatDisplayNumber");
            return "";
        }
    };
}